import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/feature/PageHeader";
import useSeo from "@/hooks/useSeo";
import { works, type WorkItem } from "@/mocks/works";

type RankTab = { key: string; name: string; filter: (w: WorkItem) => boolean };

const rankTabs: RankTab[] = [
  { key: "all", name: "综合榜", filter: () => true },
  { key: "new", name: "新番榜", filter: (w) => w.type === "new" },
  { key: "movie", name: "剧场版榜", filter: (w) => w.type === "movie" },
  { key: "ova", name: "OVA榜", filter: (w) => w.type === "ova" },
  { key: "classic", name: "经典榜", filter: (w) => w.type === "classic" },
];

const medal = [
  "bg-gradient-to-br from-secondary-400 to-primary-500 text-background-50",
  "bg-gradient-to-br from-foreground-400 to-foreground-600 text-background-50",
  "bg-gradient-to-br from-primary-500 to-primary-600 text-background-50",
];

export default function RankingPage() {
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [active, setActive] = useState("all");

  const list = useMemo(() => {
    const tab = rankTabs.find((t) => t.key === active) || rankTabs[0];
    let result = works.filter(tab.filter);
    if (q.trim()) {
      const kw = q.trim().toLowerCase();
      result = result.filter(
        (w) =>
          w.title.toLowerCase().includes(kw) ||
          w.category.includes(q.trim()) ||
          w.tags.some((t) => t.includes(q.trim()))
      );
    }
    return [...result].sort((a, b) => b.score - a.score);
  }, [active, q]);

  const title = q
    ? `「${q}」搜索结果 - 动漫热门排行 | 漫番志`
    : `动漫热门排行 - 新番剧场版OVA榜单 | 漫番志`;

  useSeo({
    title,
    description: q
      ? `为你找到「${q}」相关的动漫作品，按口碑热度实时排序，快速定位值得追的作品。`
      : "漫番志热门排行提供综合榜、新番榜、剧场版榜、OVA榜与经典榜，实时更新动漫口碑热度，帮你快速定位当下值得追的作品。",
    keywords: "动漫排行,新番排行,剧场版排行,OVA排行,热门动漫,追番榜单",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://freshanime.github.io/" },
          { "@type": "ListItem", position: 2, name: "热门排行" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "动漫热门排行",
        description: "新番、剧场版、OVA、经典番剧口碑热度实时排行。",
      },
    ],
  });

  return (
    <Layout>
      <PageHeader
        title={q ? `「${q}」的搜索结果` : "动漫热门排行"}
        subtitle={
          q
            ? `共找到 ${list.length} 部与「${q}」相关的作品，按口碑热度排序。`
            : "综合口碑热度实时滚动，涵盖新番、剧场版、OVA与经典，帮你快速定位当下值得追的作品。"
        }
        eyebrow="实时榜单"
        crumbs={[{ label: "热门排行" }]}
      />

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8 flex flex-wrap items-center gap-2">
          {rankTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActive(t.key)}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm transition-colors ${
                active === t.key
                  ? "bg-primary-500 font-bold text-background-50"
                  : "bg-background-100 text-foreground-600 ring-1 ring-background-300/60 hover:text-foreground-950"
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {list.slice(0, 10).map((w, i) => (
            <Link
              key={w.id}
              to={`/works/${w.id}`}
              className="group flex items-center gap-4 rounded-2xl bg-background-100 p-3.5 ring-1 ring-background-300/60 transition-all hover:-translate-y-0.5 hover:ring-primary-500/60 md:gap-6 md:p-4"
            >
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-heading text-lg ${
                  i < 3 ? medal[i] : "bg-background-200 text-foreground-500"
                }`}
              >
                {i + 1}
              </span>
              <img
                src={w.image}
                alt={`${w.title} 排行海报`}
                title={`${w.title} - 动漫热门排行第${i + 1}名`}
                className="h-20 w-14 shrink-0 rounded-lg object-cover object-top md:h-24 md:w-16"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate text-base font-bold text-foreground-950 group-hover:text-primary-400">
                    {w.title}
                  </h3>
                  <span className="rounded-full bg-accent-500/20 px-2 py-0.5 text-[11px] text-accent-400">
                    {w.category}番
                  </span>
                </div>
                <p className="mt-1 text-xs text-foreground-500">
                  {w.episodes} · {w.studio}
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-1.5 max-w-xs flex-1 overflow-hidden rounded-full bg-background-200">
                    <div
                      className={`h-full rounded-full ${i < 3 ? "bg-gradient-to-r from-secondary-400 to-primary-500" : "bg-accent-500"}`}
                      style={{ width: `${(w.score / 10) * 100}%` }}
                    />
                  </div>
                  <span className="whitespace-nowrap text-sm font-bold text-secondary-400">
                    {w.score.toFixed(1)}
                  </span>
                </div>
              </div>
              <span className="hidden shrink-0 items-center gap-1 text-sm font-bold text-primary-400 opacity-0 transition-opacity group-hover:opacity-100 sm:flex">
                详情 <i className="ri-arrow-right-line" />
              </span>
            </Link>
          ))}
        </div>

        {list.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl bg-background-100 py-20 text-center ring-1 ring-background-300/60">
            <i className="ri-emotion-sad-line text-4xl text-foreground-500" />
            <p className="mt-4 text-sm text-foreground-600">没有找到相关作品，换个关键词试试吧。</p>
            <Link
              to="/ranking"
              className="mt-4 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-bold text-background-50 hover:bg-primary-600"
            >
              查看完整排行
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}